import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import { bugun } from "../lib/sabitler";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Skeleton } from "./ui/skeleton";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  User,
  FileText,
  CalendarDays,
  HandCoins,
  Scale,
  Loader2,
  Trash2,
  FileUp,
  Eye,
  Save,
  AlertTriangle,
  Plus,
  X,
} from "lucide-react";

const BELGE_TURLERI_P = [
  "İş Sözleşmesi",
  "SGK Belgesi",
  "Kimlik Belgesi",
  "Sağlık Raporu",
  "Sertifika",
  "Diploma",
  "İzin Kâğıdı",
  "Tutanak",
  "Diğer Evrak",
];

const IZIN_TURLERI_P = ["Yıllık İzin", "Ücretsiz İzin", "Raporlu (İstirahat)", "Mazeret İzni", "Doğum İzni", "Diğer"];

const BELGE_SIRALAMA = [
  { value: "ad_az", label: "Dosya adı A–Z" },
  { value: "ad_za", label: "Dosya adı Z–A" },
  { value: "yeni", label: "En yeni" },
  { value: "eski", label: "En eski" },
];

const OZLUK_BOS = {
  ad_soyad: "", tc_no: "", dogum_tarihi: "", ise_giris_tarihi: "", pozisyon: "",
  bolum: "", telefon: "", adres: "", acil_kisi: "", acil_telefon: "", iban: "", aktif: true,
};
export const PersonelKartDialog = ({ target, onClose, onSaved }) => {
  const yeniMi = target === "yeni";
  const personelId = !yeniMi && target ? target.id : null;
  const [tab, setTab] = useState("ozluk");

  useEffect(() => {
    if (target) setTab("ozluk");
  }, [target]);

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="personel-kart-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {yeniMi ? "Yeni Personel" : `Personel Kartı — ${target?.ad_soyad || ""}`}
          </DialogTitle>
          <DialogDescription>
            {yeniMi ? "Özlük bilgilerini girin. Belge, izin ve avans işlemleri kayıt sonrası açılır." : "Özlük, belgeler, izin, avans ve kıdem bilgileri."}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex w-full flex-wrap h-auto gap-1 justify-start">
            <TabsTrigger value="ozluk" className="gap-1 min-h-[38px]" data-testid="pkart-tab-ozluk">
              <User className="h-3.5 w-3.5" /> Özlük
            </TabsTrigger>
            <TabsTrigger value="belgeler" disabled={yeniMi} className="gap-1 min-h-[38px]" data-testid="pkart-tab-belgeler">
              <FileText className="h-3.5 w-3.5" /> Belgeler
            </TabsTrigger>
            <TabsTrigger value="izin" disabled={yeniMi} className="gap-1 min-h-[38px]" data-testid="pkart-tab-izin">
              <CalendarDays className="h-3.5 w-3.5" /> İzin
            </TabsTrigger>
            <TabsTrigger value="avans" disabled={yeniMi} className="gap-1 min-h-[38px]" data-testid="pkart-tab-avans">
              <HandCoins className="h-3.5 w-3.5" /> Avans
            </TabsTrigger>
            <TabsTrigger value="kidem" disabled={yeniMi} className="gap-1 min-h-[38px]" data-testid="pkart-tab-kidem">
              <Scale className="h-3.5 w-3.5" /> Kıdem
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ozluk" className="mt-4">
            <OzlukSekmesi target={target} yeniMi={yeniMi} onSaved={onSaved} onClose={onClose} />
          </TabsContent>
          <TabsContent value="belgeler" className="mt-4">
            {personelId && <BelgelerSekmesi personelId={personelId} />}
          </TabsContent>
          <TabsContent value="izin" className="mt-4">
            {personelId && <IzinSekmesi personelId={personelId} />}
          </TabsContent>
          <TabsContent value="avans" className="mt-4">
            {personelId && <AvansSekmesi personelId={personelId} />}
          </TabsContent>
          <TabsContent value="kidem" className="mt-4">
            {personelId && <KidemSekmesi personelId={personelId} />}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- ÇALIŞTIĞI BÖLÜMLER (çoklu seçim + yönetilebilir liste) ---------- */
function BolumlerAlani({ secili, onChange }) {
  const [liste, setListe] = useState([]);
  const [yeni, setYeni] = useState("");
  const yukle = () => api.get("/personel-bolumleri").then((r) => setListe(r.data || [])).catch(() => {});
  useEffect(() => { yukle(); }, []);

  const toggle = (ad) => onChange(secili.includes(ad) ? secili.filter((x) => x !== ad) : [...secili, ad]);

  const ekle = async () => {
    if (!yeni.trim()) return;
    try {
      const res = await api.post("/personel-bolumleri", { ad: yeni.trim() });
      toast.success(`"${res.data.ad}" bölümü listeye eklendi.`);
      setYeni("");
      yukle();
    } catch (err) {
      toast.error(getErrMsg(err, "Bölüm eklenemedi."));
    }
  };

  const sil = async (b) => {
    try {
      await api.delete(`/personel-bolumleri/${b.id}`);
      toast.success(`"${b.ad}" bölümü silindi.`);
      onChange(secili.filter((x) => x !== b.ad));
      yukle();
    } catch (err) {
      toast.error(getErrMsg(err, "Bölüm silinemedi."));
    }
  };

  return (
    <div className="rounded-lg border bg-muted/20 p-3 space-y-2.5" data-testid="pkart-bolumler-alani">
      <div>
        <Label className="text-xs">Çalıştığı Bölümler</Label>
        <p className="text-[11px] text-muted-foreground">Birden fazla bölüm seçilebilir. Ciro girişindeki usta kırılımı bu atamadan beslenir.</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {liste.map((b) => {
          const aktif = secili.includes(b.ad);
          return (
            <span key={b.id} className="inline-flex items-center">
              <button
                type="button"
                onClick={() => toggle(b.ad)}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${
                  aktif
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white"
                    : "border-border bg-card text-foreground/80 hover:bg-muted/60"
                }`}
                data-testid={`pkart-bolum-${b.ad}`}
              >
                {b.ad}
              </button>
              {!b.sistem && (
                <button
                  type="button"
                  onClick={() => sil(b)}
                  className="ml-0.5 text-muted-foreground hover:text-red-600"
                  title={`"${b.ad}" bölümünü listeden sil`}
                  data-testid={`pkart-bolum-sil-${b.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={yeni}
          onChange={(e) => setYeni(e.target.value)}
          placeholder="Yeni bölüm ekle (örn. Beyin Tamiri)"
          className="h-10 w-56 bg-card"
          data-testid="pkart-yeni-bolum-input"
        />
        <Button type="button" variant="outline" size="sm" onClick={ekle} className="min-h-[40px]" data-testid="pkart-yeni-bolum-ekle">
          Ekle
        </Button>
      </div>
    </div>
  );
}

/* ---------- ÖZLÜK ---------- */
function OzlukSekmesi({ target, yeniMi, onSaved, onClose }) {
  const [form, setForm] = useState({ ...OZLUK_BOS, bolumler: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target && target !== "yeni") {
      const f = { ...OZLUK_BOS };
      Object.keys(OZLUK_BOS).forEach((k) => {
        if (k === "aktif") f.aktif = target.aktif !== false;
        else f[k] = target[k] || "";
      });
      f.bolumler = target.bolumler || (target.bolum ? [target.bolum] : []);
      setForm(f);
    } else {
      setForm({ ...OZLUK_BOS, bolumler: [] });
    }
  }, [target]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const kaydet = async () => {
    if (!form.ad_soyad.trim()) return toast.error("Ad Soyad zorunludur.");
    if (form.tc_no && !/^\d{11}$/.test(form.tc_no.trim())) return toast.error("TC Kimlik No 11 haneli rakam olmalıdır.");
    setSaving(true);
    try {
      const payload = {};
      Object.keys(OZLUK_BOS).forEach((k) => {
        payload[k] = k === "aktif" ? form.aktif : (String(form[k] || "").trim() || null);
      });
      payload.ad_soyad = form.ad_soyad.trim();
      payload.bolumler = form.bolumler;
      payload.bolum = form.bolumler[0] || payload.bolum || null;
      if (yeniMi) {
        await api.post("/personeller", payload);
        toast.success("Personel eklendi.");
      } else {
        await api.put(`/personeller/${target.id}`, payload);
        toast.success("Personel kartı güncellendi.");
      }
      onSaved();
    } catch (err) {
      toast.error(getErrMsg(err, "Personel kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  const alan = (label, key, props = {}) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        className="h-11"
        data-testid={`pkart-ozluk-${key}`}
        {...props}
      />
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {alan("Ad Soyad *", "ad_soyad", { placeholder: "Ad Soyad" })}
        {alan("TC Kimlik No", "tc_no", { placeholder: "11 haneli", inputMode: "numeric", maxLength: 11 })}
        {alan("Doğum Tarihi", "dogum_tarihi", { type: "date" })}
        {alan("İşe Giriş Tarihi", "ise_giris_tarihi", { type: "date" })}
        {alan("Pozisyon", "pozisyon", { placeholder: "Örn: Boya Ustası" })}
        {alan("Telefon", "telefon", { placeholder: "05xx xxx xx xx", inputMode: "tel" })}
        {alan("IBAN", "iban", { placeholder: "TR.." })}
        {alan("Acil Durum Kişisi", "acil_kisi", { placeholder: "Ad Soyad" })}
        {alan("Acil Durum Telefonu", "acil_telefon", { placeholder: "05xx xxx xx xx", inputMode: "tel" })}
      </div>
      <BolumlerAlani secili={form.bolumler || []} onChange={(b) => set("bolumler", b)} />
      <div className="space-y-1">
        <Label className="text-xs">Adres</Label>
        <Textarea
          value={form.adres}
          onChange={(e) => set("adres", e.target.value)}
          rows={2}
          placeholder="Açık adres"
          data-testid="pkart-ozluk-adres"
        />
      </div>
      <div className="flex items-center justify-between rounded-lg border p-3">
        <div>
          <Label>Aktif</Label>
          <p className="text-xs text-muted-foreground">Pasif personel ücret tablosunda görünmez.</p>
        </div>
        <Switch checked={form.aktif} onCheckedChange={(v) => set("aktif", v)} data-testid="pkart-ozluk-aktif" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} data-testid="pkart-ozluk-vazgec">Vazgeç</Button>
        <Button onClick={kaydet} disabled={saving} className="min-w-[130px] gap-1.5" data-testid="pkart-ozluk-kaydet">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
        </Button>
      </div>
    </div>
  );
}

/* ---------- BELGELER ---------- */
function BelgelerSekmesi({ personelId }) {
  const [belgeler, setBelgeler] = useState(null);
  const [belgeTuru, setBelgeTuru] = useState("İş Sözleşmesi");
  const [sirala, setSirala] = useState("ad_az");
  const [ekstra, setEkstra] = useState({ belge_adi: "", aciklama: "", belge_tarihi: "" });
  const [yukleniyor, setYukleniyor] = useState(false);
  const fileRef = useRef(null);

  const digerEvrak = belgeTuru === "Diğer Evrak";

  const fetchBelgeler = useCallback(async () => {
    try {
      const res = await api.get(`/personeller/${personelId}/belgeler`, { params: { sirala } });
      setBelgeler(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Belgeler yüklenemedi."));
    }
  }, [personelId, sirala]);

  useEffect(() => {
    fetchBelgeler();
  }, [fetchBelgeler]);

  const yukleBaslat = () => {
    if (digerEvrak && !ekstra.belge_adi.trim()) {
      return toast.error("Diğer evrak için belge adı zorunludur.");
    }
    fileRef.current?.click();
  };

  const yukle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setYukleniyor(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("belge_turu", belgeTuru);
      if (digerEvrak) {
        fd.append("belge_adi", ekstra.belge_adi.trim());
        fd.append("aciklama", ekstra.aciklama.trim());
        fd.append("belge_tarihi", ekstra.belge_tarihi || "");
      }
      await api.post(`/personeller/${personelId}/belge`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Belge yüklendi.");
      setEkstra({ belge_adi: "", aciklama: "", belge_tarihi: "" });
      fetchBelgeler();
    } catch (err) {
      toast.error(getErrMsg(err, "Belge yüklenemedi."));
    } finally {
      setYukleniyor(false);
    }
  };

  const ac = async (id) => {
    try {
      const res = await api.get(`/personel-belge/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      window.open(url, "_blank", "noopener");
    } catch {
      toast.error("Belge açılamadı.");
    }
  };

  const sil = async (id) => {
    try {
      await api.delete(`/personel-belge/${id}`);
      toast.success("Belge silindi.");
      fetchBelgeler();
    } catch (err) {
      toast.error(getErrMsg(err, "Belge silinemedi."));
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2 rounded-lg border border-dashed p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Belge Türü</Label>
            <Select value={belgeTuru} onValueChange={setBelgeTuru}>
              <SelectTrigger className="h-10 w-48" data-testid="pkart-belge-turu-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {BELGE_TURLERI_P.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" onChange={yukle} className="hidden" data-testid="pkart-belge-file-input" />
          <Button onClick={yukleBaslat} disabled={yukleniyor} className="h-10 gap-1.5" data-testid="pkart-belge-yukle-button">
            {yukleniyor ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Belge Yükle
          </Button>
        </div>
        {digerEvrak && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="pkart-belge-diger-alanlar">
            <div className="space-y-1">
              <Label className="text-xs">Belge Adı *</Label>
              <Input
                value={ekstra.belge_adi}
                onChange={(e) => setEkstra((x) => ({ ...x, belge_adi: e.target.value }))}
                placeholder="Örn: Zimmet Formu"
                className="h-10"
                data-testid="pkart-belge-adi-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Belge Tarihi</Label>
              <Input
                type="date"
                value={ekstra.belge_tarihi}
                onChange={(e) => setEkstra((x) => ({ ...x, belge_tarihi: e.target.value }))}
                className="h-10"
                data-testid="pkart-belge-tarihi-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Açıklama</Label>
              <Input
                value={ekstra.aciklama}
                onChange={(e) => setEkstra((x) => ({ ...x, aciklama: e.target.value }))}
                placeholder="Opsiyonel"
                className="h-10"
                data-testid="pkart-belge-aciklama-input"
              />
            </div>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">PDF, JPG, PNG, WEBP, HEIC — en fazla 10 MB.</p>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {belgeler ? `${belgeler.length} belge` : ""}
        </p>
        <Select value={sirala} onValueChange={setSirala}>
          <SelectTrigger className="h-9 w-40 text-xs" data-testid="pkart-belge-sirala-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card">
            {BELGE_SIRALAMA.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!belgeler ? (
        <Skeleton className="h-24" />
      ) : belgeler.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center" data-testid="pkart-belge-bos">Henüz belge yüklenmedi.</p>
      ) : (
        <div className="space-y-2" data-testid="pkart-belge-liste">
          {belgeler.map((b) => (
            <div key={b.id} className="flex items-center gap-3 rounded-lg border px-3 py-2.5" data-testid={`pkart-belge-${b.id}`}>
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{b.belge_adi || b.dosya_adi}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  <Badge className="mr-1 bg-muted text-foreground/70 border border-border hover:bg-muted text-[10px] px-1.5 py-0">{b.belge_turu}</Badge>
                  {b.belge_tarihi ? `${formatDate(b.belge_tarihi)} • ` : ""}
                  {formatDate(b.created_at?.slice(0, 10))} • {(b.boyut / 1024).toFixed(0)} KB
                  {b.aciklama ? ` • ${b.aciklama}` : ""}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => ac(b.id)} className="h-8 w-8" data-testid={`pkart-belge-ac-${b.id}`} aria-label="Belgeyi aç">
                <Eye className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => sil(b.id)} className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`pkart-belge-sil-${b.id}`} aria-label="Belgeyi sil">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- İZİN ---------- */
function IzinSekmesi({ personelId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ baslangic: "", bitis: "", izin_turu: "Yıllık İzin", aciklama: "" });
  const [izinDosya, setIzinDosya] = useState(null);
  const [saving, setSaving] = useState(false);
  const izinFileRef = useRef(null);

  const fetchIzinler = useCallback(async () => {
    try {
      const res = await api.get(`/personeller/${personelId}/izinler`);
      setData(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "İzin bilgileri yüklenemedi."));
    }
  }, [personelId]);

  useEffect(() => {
    fetchIzinler();
  }, [fetchIzinler]);

  const ekle = async () => {
    if (!form.baslangic || !form.bitis) return toast.error("Başlangıç ve bitiş tarihi zorunludur.");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("baslangic", form.baslangic);
      fd.append("bitis", form.bitis);
      fd.append("izin_turu", form.izin_turu);
      fd.append("aciklama", form.aciklama);
      if (izinDosya) fd.append("file", izinDosya);
      await api.post(`/personeller/${personelId}/izin`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(izinDosya ? "İzin kaydedildi — izin kâğıdı Belgeler sekmesine eklendi." : "İzin kaydedildi.");
      setForm({ baslangic: "", bitis: "", izin_turu: "Yıllık İzin", aciklama: "" });
      setIzinDosya(null);
      fetchIzinler();
    } catch (err) {
      toast.error(getErrMsg(err, "İzin kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  const sil = async (id) => {
    try {
      await api.delete(`/izin/${id}`);
      toast.success("İzin kaydı silindi.");
      fetchIzinler();
    } catch (err) {
      toast.error(getErrMsg(err, "İzin silinemedi."));
    }
  };

  const belgeAc = async (belgeId) => {
    try {
      const res = await api.get(`/personel-belge/${belgeId}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      window.open(url, "_blank", "noopener");
    } catch {
      toast.error("İzin kâğıdı açılamadı.");
    }
  };

  if (!data) return <Skeleton className="h-40" />;
  const o = data.ozet;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pkart-izin-ozet">
        {[
          { label: "Kıdem", deger: o.kidem_yili != null ? `${o.kidem_yili.toLocaleString("tr-TR")} yıl` : "—", testid: "pkart-izin-kidem" },
          { label: `Hak Ediş (${o.yil})`, deger: `${o.hak_edis} gün`, testid: "pkart-izin-hak" },
          { label: "Kullanılan", deger: `${o.kullanilan} gün`, testid: "pkart-izin-kullanilan" },
          { label: "Kalan", deger: `${o.kalan} gün`, kirmizi: o.kalan < 0, testid: "pkart-izin-kalan" },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border p-2.5" data-testid={s.testid}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</p>
            <p className={`font-display text-sm font-bold ${s.kirmizi ? "text-red-700" : ""}`}>{s.deger}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Hak ediş kıdeme göre otomatik: 1-5 yıl 14 gün • 5-15 yıl 20 gün • 15+ yıl 26 gün. Hak edişten yalnızca <strong>Yıllık İzin</strong> günleri düşülür.
      </p>

      <div className="space-y-2 rounded-lg border border-dashed p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Başlangıç *</Label>
            <Input type="date" value={form.baslangic} onChange={(e) => setForm((f) => ({ ...f, baslangic: e.target.value }))} className="h-10" data-testid="pkart-izin-baslangic" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Bitiş *</Label>
            <Input type="date" value={form.bitis} onChange={(e) => setForm((f) => ({ ...f, bitis: e.target.value }))} className="h-10" data-testid="pkart-izin-bitis" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">İzin Türü *</Label>
            <Select value={form.izin_turu} onValueChange={(v) => setForm((f) => ({ ...f, izin_turu: v }))}>
              <SelectTrigger className="h-10" data-testid="pkart-izin-turu-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {IZIN_TURLERI_P.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Açıklama</Label>
            <Input value={form.aciklama} onChange={(e) => setForm((f) => ({ ...f, aciklama: e.target.value }))} placeholder="Opsiyonel" className="h-10" data-testid="pkart-izin-aciklama" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={izinFileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
            onChange={(e) => { setIzinDosya(e.target.files?.[0] || null); e.target.value = ""; }}
            className="hidden"
            data-testid="pkart-izin-file-input"
          />
          <Button type="button" variant="outline" onClick={() => izinFileRef.current?.click()} className="h-10 gap-1.5" data-testid="pkart-izin-kagit-sec-button">
            <FileUp className="h-4 w-4" /> İmzalı İzin Kâğıdı Yükle
          </Button>
          {izinDosya ? (
            <span className="flex items-center gap-1.5 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs" data-testid="pkart-izin-secili-dosya">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="max-w-[180px] truncate">{izinDosya.name}</span>
              <button type="button" onClick={() => setIzinDosya(null)} className="text-destructive hover:opacity-80" aria-label="Dosyayı kaldır" data-testid="pkart-izin-dosya-kaldir">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground">Opsiyonel — yüklenen kâğıt otomatik olarak Belgeler sekmesine eklenir.</span>
          )}
          <Button onClick={ekle} disabled={saving} className="ml-auto h-10 gap-1" data-testid="pkart-izin-ekle-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ekle
          </Button>
        </div>
      </div>

      {data.izinler.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3 text-center" data-testid="pkart-izin-bos">Bu yıl izin kaydı yok.</p>
      ) : (
        <div className="space-y-2" data-testid="pkart-izin-liste">
          {data.izinler.map((i) => (
            <div key={i.id} className="flex items-center gap-3 rounded-lg border px-3 py-2" data-testid={`pkart-izin-${i.id}`}>
              <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">
                  {formatDate(i.baslangic)} — {formatDate(i.bitis)}
                  <Badge className="ml-1 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-50 text-[10px]">{i.gun_sayisi} gün</Badge>
                  <Badge className="ml-1 bg-muted text-foreground/70 border border-border hover:bg-muted text-[10px]">{i.izin_turu || "Yıllık İzin"}</Badge>
                </p>
                {i.aciklama && <p className="text-[11px] text-muted-foreground truncate">{i.aciklama}</p>}
              </div>
              {i.belge_id && (
                <Button variant="ghost" size="icon" onClick={() => belgeAc(i.belge_id)} className="h-8 w-8" data-testid={`pkart-izin-belge-ac-${i.id}`} aria-label="İzin kâğıdını aç" title="İmzalı izin kâğıdını görüntüle">
                  <Eye className="h-4 w-4" />
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => sil(i.id)} className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`pkart-izin-sil-${i.id}`} aria-label="İzni sil">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- AVANS ---------- */
function AvansSekmesi({ personelId }) {
  const [avanslar, setAvanslar] = useState(null);
  const [form, setForm] = useState({ tarih: bugun(), tutar: "", odeme_yeri: "Kasa", aciklama: "" });
  const [saving, setSaving] = useState(false);

  const fetchAvanslar = useCallback(async () => {
    try {
      const res = await api.get("/avanslar", { params: { personel_id: personelId } });
      setAvanslar(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Avanslar yüklenemedi."));
    }
  }, [personelId]);

  useEffect(() => {
    fetchAvanslar();
  }, [fetchAvanslar]);

  const ekle = async () => {
    const tutar = parseFloat(form.tutar);
    if (!tutar || tutar <= 0) return toast.error("Avans tutarı 0'dan büyük olmalıdır.");
    if (!form.tarih) return toast.error("Tarih zorunludur.");
    setSaving(true);
    try {
      await api.post(`/personeller/${personelId}/avans`, { ...form, tutar });
      toast.success("Avans kaydedildi. Ay içi net ödemeden düşülecek.");
      setForm({ tarih: bugun(), tutar: "", odeme_yeri: "Kasa", aciklama: "" });
      fetchAvanslar();
    } catch (err) {
      toast.error(getErrMsg(err, "Avans kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  const sil = async (id) => {
    try {
      await api.delete(`/avans/${id}`);
      toast.success("Avans silindi.");
      fetchAvanslar();
    } catch (err) {
      toast.error(getErrMsg(err, "Avans silinemedi."));
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground">
        Verilen avans, ilgili ayın net ödemesinden otomatik düşülür ve kasa/banka çıkışı olarak kaydedilir.
      </p>
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed p-3">
        <div className="space-y-1">
          <Label className="text-xs">Tarih *</Label>
          <Input type="date" value={form.tarih} onChange={(e) => setForm((f) => ({ ...f, tarih: e.target.value }))} className="h-10" data-testid="pkart-avans-tarih" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tutar * ₺</Label>
          <Input type="number" inputMode="decimal" min="0" step="0.01" value={form.tutar} onChange={(e) => setForm((f) => ({ ...f, tutar: e.target.value }))} placeholder="0,00" className="h-10 w-32 text-right tabular-nums" data-testid="pkart-avans-tutar" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Nereden Ödendi</Label>
          <Select value={form.odeme_yeri} onValueChange={(v) => setForm((f) => ({ ...f, odeme_yeri: v }))}>
            <SelectTrigger className="h-10 w-28" data-testid="pkart-avans-odeme-yeri">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="Kasa">Kasa</SelectItem>
              <SelectItem value="Banka">Banka</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 flex-1 min-w-[130px]">
          <Label className="text-xs">Açıklama</Label>
          <Input value={form.aciklama} onChange={(e) => setForm((f) => ({ ...f, aciklama: e.target.value }))} placeholder="Opsiyonel" className="h-10" data-testid="pkart-avans-aciklama" />
        </div>
        <Button onClick={ekle} disabled={saving} className="h-10 gap-1" data-testid="pkart-avans-ekle-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ekle
        </Button>
      </div>

      {!avanslar ? (
        <Skeleton className="h-24" />
      ) : avanslar.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3 text-center" data-testid="pkart-avans-bos">Avans kaydı yok.</p>
      ) : (
        <div className="space-y-2" data-testid="pkart-avans-liste">
          {avanslar.map((a) => (
            <div key={a.id} className="flex items-center gap-3 rounded-lg border px-3 py-2" data-testid={`pkart-avans-${a.id}`}>
              <HandCoins className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold tabular-nums">{formatTL(a.tutar, 2)}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {formatDate(a.tarih)} • {a.odeme_yeri}{a.aciklama ? ` • ${a.aciklama}` : ""}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => sil(a.id)} className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`pkart-avans-sil-${a.id}`} aria-label="Avansı sil">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- KIDEM TAZMİNATI ---------- */
function KidemSekmesi({ personelId }) {
  const [sonuc, setSonuc] = useState(null);
  const [hesaplaniyor, setHesaplaniyor] = useState(false);

  const hesapla = async () => {
    setHesaplaniyor(true);
    try {
      const res = await api.get(`/personeller/${personelId}/kidem-hesap`);
      setSonuc(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Kıdem hesabı yapılamadı."));
    } finally {
      setHesaplaniyor(false);
    }
  };

  return (
    <div className="space-y-3">
      <Button onClick={hesapla} disabled={hesaplaniyor} className="min-h-[44px] gap-1.5" data-testid="pkart-kidem-hesapla-button">
        {hesaplaniyor ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scale className="h-4 w-4" />}
        Kıdem Tazminatı Hesapla
      </Button>

      {sonuc && (
        <div className="space-y-3" data-testid="pkart-kidem-sonuc">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { label: "Kıdem", deger: `${sonuc.kidem_yili.toLocaleString("tr-TR")} yıl` },
              { label: "Son Brüt Ücret", deger: formatTL(sonuc.son_brut) },
              { label: "Esas Ücret", deger: formatTL(sonuc.esas_ucret) },
              { label: "Brüt Tazminat", deger: formatTL(sonuc.brut_tazminat) },
              { label: "Damga Vergisi", deger: formatTL(sonuc.damga_vergisi) },
              { label: "Net Tazminat (Tahmini)", deger: formatTL(sonuc.net_tazminat), vurgu: true },
            ].map((s) => (
              <div key={s.label} className={`rounded-lg border p-2.5 ${s.vurgu ? "border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/10" : ""}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</p>
                <p className="font-display text-sm font-bold tabular-nums" data-testid={s.vurgu ? "pkart-kidem-net" : undefined}>{s.deger}</p>
              </div>
            ))}
          </div>
          {sonuc.tavan_uygulandi && (
            <p className="text-xs text-amber-800 rounded-md border border-amber-300 bg-amber-50/70 px-3 py-2" data-testid="pkart-kidem-tavan-uyari">
              Son brüt ücret kıdem tavanını ({formatTL(sonuc.kidem_tavani)}) aştığı için hesapta tavan tutarı esas alındı.
            </p>
          )}
          <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground rounded-md border px-3 py-2" data-testid="pkart-kidem-not">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            {sonuc.not}
          </p>
        </div>
      )}
    </div>
  );
}
